import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useGeoMarkupStore } from '@/store';
import { useArtifactNavigation } from '@/hooks/useArtifactNavigation';
import { useDebounce } from '@/hooks/useDebounce';
import { useNotifications } from '@/hooks/useNotifications';
import { NoteVisibility } from '@/types/notes';
import type { CreateNoteData, UpdateNoteData } from '@/types/notes';
import type { AutoSaveStatus } from '@/components/common';
import {
  NOTE_DEFAULT_COLOR,
  NOTE_AUTOSAVE_DEBOUNCE_MS,
} from '@/constants/notes';

const AUTOSAVE_INDICATOR_TIMEOUT = 2000;
const EMPTY_TIPTAP_DOC = '{"type":"doc","content":[{"type":"paragraph"}]}';

function hasRealContent(content: string | null): boolean {
  return !!content && content !== EMPTY_TIPTAP_DOC;
}

interface NoteFormState {
  title: string;
  content: string | null;
  color: string;
  visibility: NoteVisibility;
  latitude: number | null;
  longitude: number | null;
  location: string;
  linkedTaskId: string | null;
}

interface UseNoteFormReturn {
  formState: NoteFormState;
  isOpen: boolean;
  isCreateMode: boolean;
  isSyncing: boolean;
  setTitle: (value: string) => void;
  setContent: (value: string) => void;
  setColor: (value: string) => void;
  setVisibility: (value: string) => void;
  setLocation: (value: string) => void;
  setLinkedTaskId: (value: string | null) => void;
  setCoordinates: (coords: { lat: number; lng: number }) => void;
  handleSave: () => Promise<void>;
  handleClose: () => void;
  handlePinToMap: () => void;
  handleRemoveAnchorPoint: () => void;
  handleImageUpload: (file: File) => Promise<string>;
  markAsEdited: () => void;
  autoSaveStatus: AutoSaveStatus;
  hasLocation: boolean;
}

interface NoteDataFields {
  title: string;
  content: string | null | undefined;
  color: string;
  visibility: NoteVisibility;
  latitude: number | null;
  longitude: number | null;
  location: string;
  linkedTaskId: string | null;
}

function buildBaseNoteData(f: NoteDataFields) {
  return {
    title: f.title,
    content: f.content ?? undefined,
    color: f.color,
    visibility: f.visibility,
    latitude: f.latitude ?? undefined,
    longitude: f.longitude ?? undefined,
    location: f.location || undefined,
    taskIds: f.linkedTaskId ? [f.linkedTaskId] : [],
  };
}

function buildCreateData(f: NoteDataFields): CreateNoteData {
  return buildBaseNoteData(f);
}

function buildUpdateData(f: NoteDataFields): UpdateNoteData {
  return {
    ...buildBaseNoteData(f),
    removeAnchorPoint: f.latitude === null && f.longitude === null,
  };
}

export function useNoteForm(): UseNoteFormReturn {
  const { t } = useTranslation();
  const { showError } = useNotifications();
  const isOpen = useGeoMarkupStore((state) => state.notes.formIsOpen);
  const mode = useGeoMarkupStore((state) => state.notes.formMode);
  const editingNoteId = useGeoMarkupStore((state) => state.notes.formEditingId);
  const anchorPointLat = useGeoMarkupStore(
    (state) => state.notes.formAnchorPoint?.lat ?? null
  );
  const anchorPointLng = useGeoMarkupStore(
    (state) => state.notes.formAnchorPoint?.lng ?? null
  );
  const isSyncing = useGeoMarkupStore((state) => state.notes.isSyncing);

  const createNote = useGeoMarkupStore((state) => state.notes.createNote);
  const updateNote = useGeoMarkupStore((state) => state.notes.updateNote);
  const uploadImage = useGeoMarkupStore((state) => state.notes.uploadImage);

  const { openArtifact, closeOverlay } = useArtifactNavigation();
  const startNoteLocationSelection = useGeoMarkupStore(
    (state) => state.ui.startNoteLocationSelection
  );
  const setNoteFormAnchorPoint = useGeoMarkupStore(
    (state) => state.notes.setFormAnchorPoint
  );

  const existingNote = useGeoMarkupStore((state) =>
    editingNoteId
      ? (state.notes.items.find(
          (note) => note.id === editingNoteId || note.shortId === editingNoteId
        ) ?? null)
      : null
  );
  const detailedNote = useGeoMarkupStore((state) => state.notes.detailedNote);
  const fetchNoteById = useGeoMarkupStore((state) => state.notes.fetchNoteById);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState<string | null>(null);
  const [color, setColor] = useState(NOTE_DEFAULT_COLOR);
  const [visibility, setVisibilityState] = useState<NoteVisibility>(
    NoteVisibility.Private
  );
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [location, setLocation] = useState('');
  const [linkedTaskId, setLinkedTaskId] = useState<string | null>(null);

  const createdNoteIdRef = useRef<string | null>(null);
  const isInitializedRef = useRef(false);
  const hasUserEditedRef = useRef(false);
  const editableAfterRef = useRef(0);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const [autoSaveStatus, setAutoSaveStatus] = useState<AutoSaveStatus>('idle');

  const isCreateMode = mode === 'create';

  // Ref for stable handleImageUpload — avoids dep chain breaking TiptapEditor memo
  const formStateRef = useRef<NoteFormState>(
    undefined as unknown as NoteFormState
  );
  formStateRef.current = {
    title,
    content,
    color,
    visibility,
    latitude,
    longitude,
    location,
    linkedTaskId,
  };

  // Fetch full note data when editing an existing note
  useEffect(() => {
    if (isOpen && editingNoteId) {
      fetchNoteById(editingNoteId);
    }
  }, [isOpen, editingNoteId, fetchNoteById]);

  useEffect(() => {
    if (!isOpen) {
      isInitializedRef.current = false;
      createdNoteIdRef.current = null;
      hasUserEditedRef.current = false;
      setAutoSaveStatus('idle');
      clearTimeout(autoSaveTimerRef.current);
      return;
    }

    if (isInitializedRef.current) return;

    // For edit mode, wait until detailedNote is loaded
    const noteForInit = editingNoteId ? detailedNote : null;
    if (editingNoteId && !noteForInit) return;

    isInitializedRef.current = true;
    editableAfterRef.current = Date.now() + NOTE_AUTOSAVE_DEBOUNCE_MS + 200;

    if (existingNote && noteForInit) {
      setTitle(existingNote.title);
      setContent(noteForInit.content || null);
      setColor(existingNote.color);
      setVisibilityState(existingNote.visibility);
      setLatitude(existingNote.latitude);
      setLongitude(existingNote.longitude);
      setLocation(existingNote.location || '');
      setLinkedTaskId(noteForInit.linkedTaskIds?.[0] ?? null);
    } else if (!editingNoteId) {
      setTitle('');
      setContent(null);
      setColor(NOTE_DEFAULT_COLOR);
      setVisibilityState(NoteVisibility.Private);
      setLatitude(anchorPointLat);
      setLongitude(anchorPointLng);
      setLocation('');
      setLinkedTaskId(null);
    }
  }, [
    isOpen,
    existingNote,
    detailedNote,
    editingNoteId,
    anchorPointLat,
    anchorPointLng,
  ]);

  useEffect(() => {
    if (!isOpen || !isInitializedRef.current) return;
    if (anchorPointLat !== null && anchorPointLng !== null) {
      setLatitude(anchorPointLat);
      setLongitude(anchorPointLng);
    }
  }, [anchorPointLat, anchorPointLng, isOpen]);

  const { debouncedValue: debouncedTitle } = useDebounce(
    title,
    NOTE_AUTOSAVE_DEBOUNCE_MS
  );
  const { debouncedValue: debouncedContent } = useDebounce(
    content,
    NOTE_AUTOSAVE_DEBOUNCE_MS
  );
  const { debouncedValue: debouncedLocation } = useDebounce(
    location,
    NOTE_AUTOSAVE_DEBOUNCE_MS
  );

  // Autosave effect — auto-creates in create mode, then updates
  useEffect(() => {
    if (!isOpen || !isInitializedRef.current) return;
    if (!debouncedTitle.trim() && !debouncedContent?.trim()) return;

    const noteTitle = debouncedTitle.trim() || t('notes.panel.newNote');
    const noteIdForUpdate = createdNoteIdRef.current ?? editingNoteId;

    if (noteIdForUpdate) {
      clearTimeout(autoSaveTimerRef.current);
      setAutoSaveStatus('saving');
      const data = buildUpdateData({
        title: noteTitle,
        content: debouncedContent,
        color,
        visibility,
        latitude,
        longitude,
        location: debouncedLocation,
        linkedTaskId,
      });

      updateNote(noteIdForUpdate, data).then((success) => {
        if (!isInitializedRef.current) return;
        setAutoSaveStatus(success ? 'saved' : 'error');
        autoSaveTimerRef.current = setTimeout(
          () => setAutoSaveStatus('idle'),
          AUTOSAVE_INDICATOR_TIMEOUT
        );
      });
    } else if (isCreateMode && hasUserEditedRef.current) {
      if (!debouncedTitle.trim() && !hasRealContent(debouncedContent)) return;

      clearTimeout(autoSaveTimerRef.current);
      setAutoSaveStatus('saving');
      const data = buildCreateData({
        title: noteTitle,
        content: debouncedContent,
        color,
        visibility,
        latitude,
        longitude,
        location: debouncedLocation,
        linkedTaskId,
      });

      createNote(data).then((newNoteId) => {
        if (!isInitializedRef.current) return;
        if (newNoteId) {
          createdNoteIdRef.current = newNoteId;
          setAutoSaveStatus('saved');
        } else {
          setAutoSaveStatus('error');
        }
        autoSaveTimerRef.current = setTimeout(
          () => setAutoSaveStatus('idle'),
          AUTOSAVE_INDICATOR_TIMEOUT
        );
      });
    } else {
      setAutoSaveStatus('idle');
    }
  }, [
    debouncedTitle,
    debouncedContent,
    debouncedLocation,
    isOpen,
    editingNoteId,
    isCreateMode,
    color,
    visibility,
    latitude,
    longitude,
    linkedTaskId,
    updateNote,
    createNote,
    t,
  ]);

  useEffect(() => {
    return () => clearTimeout(autoSaveTimerRef.current);
  }, []);

  const handleSave = useCallback(async () => {
    if (!title.trim()) return;

    if (isCreateMode && !createdNoteIdRef.current) {
      const data = buildCreateData({
        title: title.trim(),
        content,
        color,
        visibility,
        latitude,
        longitude,
        location,
        linkedTaskId,
      });

      const newNoteId = await createNote(data);
      if (newNoteId) {
        createdNoteIdRef.current = newNoteId;
        const createdNote = useGeoMarkupStore
          .getState()
          .notes.items.find((note) => note.id === newNoteId);
        openArtifact('notes', 'detail', createdNote?.shortId ?? newNoteId);
      }
    } else {
      const noteId = createdNoteIdRef.current ?? editingNoteId;
      if (!noteId) return;

      const data = buildUpdateData({
        title: title.trim(),
        content,
        color,
        visibility,
        latitude,
        longitude,
        location,
        linkedTaskId,
      });

      await updateNote(noteId, data);
      const updatedNote = useGeoMarkupStore
        .getState()
        .notes.items.find(
          (note) => note.id === noteId || note.shortId === noteId
        );
      openArtifact('notes', 'detail', updatedNote?.shortId ?? noteId);
    }
  }, [
    title,
    content,
    color,
    visibility,
    latitude,
    longitude,
    location,
    linkedTaskId,
    isCreateMode,
    editingNoteId,
    createNote,
    updateNote,
    openArtifact,
  ]);

  // Stable callback — reads form state from ref to avoid 12-dep chain breaking TiptapEditor memo
  const handleImageUpload = useCallback(
    async (file: File): Promise<string> => {
      let noteId = editingNoteId ?? createdNoteIdRef.current;

      if (!noteId && isCreateMode) {
        const state = formStateRef.current;
        const noteTitle = state.title.trim() || t('notes.panel.newNote');
        const data = buildCreateData({
          title: noteTitle,
          content: state.content,
          color: state.color,
          visibility: state.visibility,
          latitude: state.latitude,
          longitude: state.longitude,
          location: state.location,
          linkedTaskId: state.linkedTaskId,
        });
        const newNoteId = await createNote(data);
        if (!newNoteId) {
          showError(t('notes.form.imageUpload.saveFailed'));
          throw new Error('Failed to create note');
        }
        createdNoteIdRef.current = newNoteId;
        noteId = newNoteId;
        if (!state.title.trim()) {
          setTitle(noteTitle);
        }
      }

      if (!noteId) {
        showError(t('notes.form.imageUpload.noNoteId'));
        throw new Error('No note ID available');
      }

      const url = await uploadImage(noteId, file);
      if (!url) {
        showError(t('notes.form.imageUpload.uploadFailed'));
        throw new Error('Upload failed');
      }
      return url;
    },
    [editingNoteId, isCreateMode, createNote, uploadImage, showError, t]
  );

  const handleClose = useCallback(() => {
    closeOverlay();
  }, [closeOverlay]);

  const handlePinToMap = useCallback(() => {
    startNoteLocationSelection();
  }, [startNoteLocationSelection]);

  const handleRemoveAnchorPoint = useCallback(() => {
    setLatitude(null);
    setLongitude(null);
    setLocation('');
    setNoteFormAnchorPoint(null);
  }, [setNoteFormAnchorPoint]);

  const setVisibility = useCallback((value: string) => {
    setVisibilityState(value as NoteVisibility);
  }, []);

  const markAsEdited = useCallback(() => {
    if (Date.now() < editableAfterRef.current) return;
    if (hasUserEditedRef.current) return;
    hasUserEditedRef.current = true;
    // Notes are NOT reported dirty to the global unsaved-changes guard: they
    // auto-save on every change, so closing / switching never loses data and
    // must not prompt. `hasUserEditedRef` still gates the create-mode autosave.
  }, []);

  const setCoordinates = useCallback(
    (coords: { lat: number; lng: number }) => {
      setLatitude(coords.lat);
      setLongitude(coords.lng);
      setNoteFormAnchorPoint({ lat: coords.lat, lng: coords.lng });
      markAsEdited();
    },
    [setNoteFormAnchorPoint, markAsEdited]
  );

  const hasLocation = latitude !== null && longitude !== null;

  const formState: NoteFormState = {
    title,
    content,
    color,
    visibility,
    latitude,
    longitude,
    location,
    linkedTaskId,
  };

  return {
    formState,
    isOpen,
    isCreateMode,
    isSyncing,
    setTitle,
    setContent,
    setColor,
    setVisibility,
    setLocation,
    setLinkedTaskId,
    setCoordinates,
    handleSave,
    handleClose,
    handlePinToMap,
    handleRemoveAnchorPoint,
    handleImageUpload,
    markAsEdited,
    autoSaveStatus,
    hasLocation,
  };
}
